import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatMenu, MatMenuTrigger } from "@angular/material/menu";
import { MatCalendar } from "@angular/material/datepicker";
import { MatInput } from "@angular/material/input";
import { formatDate } from "@angular/common";
import { ControlValueAccessor, FormsModule, NgControl } from "@angular/forms";
import { SharedModule } from "../../shared.module";
import { asapScheduler } from "rxjs";
import { ChipsComponent } from "../chips/chips.component";
import { BigButtonComponent } from "../../components/big-button/big-button.component";

@Component({
  selector: "vl-datetime-picker",
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatIcon,
    MatMenuTrigger,
    MatMenu,
    MatCalendar,
    MatInput,
    FormsModule,
    SharedModule,
    ChipsComponent,
    BigButtonComponent,
  ],
  templateUrl: "./datetime-picker.component.html",
  styleUrl: "./datetime-picker.component.scss",
})
export class DatetimePickerComponent implements ControlValueAccessor {
  label = input<string>();
  value = model<Date>();
  mode = input<"date" | "datetime">("datetime");
  disabled = input(false);
  disabledByForm = signal(false);
  isDisabled = computed(() => this.disabled() || this.disabledByForm());

  buildedValue = signal<Date>(new Date());
  displayValue = model<string>("");

  selectedDate = signal<Date>(new Date());
  hour = model<number>(new Date().getHours());
  minute = model<number>(new Date().getMinutes());

  type = input<"input" | "chips">("input");
  dateOnly = input(false);

  private ngControl = inject(NgControl, { optional: true, self: true });

  get hasError(): boolean {
    const ctrl = this.ngControl?.control;
    return ctrl ? ctrl.invalid && ctrl.touched : false;
  }

  get isRequired(): boolean {
    const ctrl = this.ngControl?.control;
    if (!ctrl?.validator) return false;
    const validation = ctrl.validator({} as any);
    return validation?.["required"] ?? false;
  }

  buttonState = computed(() => (this.value() ? "active" : "enabled"));
  menuTrigger = viewChild(MatMenuTrigger);

  // Inputs pour la saisie de date séparée
  dayInput = viewChild<ElementRef<HTMLInputElement>>("dayInput");
  monthInput = viewChild<ElementRef<HTMLInputElement>>("monthInput");
  yearInput = viewChild<ElementRef<HTMLInputElement>>("yearInput");
  hourInput = viewChild<ElementRef<HTMLInputElement>>("hourInput");
  minuteInput = viewChild<ElementRef<HTMLInputElement>>("minuteInput");

  // Valeurs des champs de date
  dayValue = computed(() => {
    const date = this.value();
    if (!date) return "";
    return String(new Date(date).getDate()).padStart(2, "0");
  });

  monthValue = computed(() => {
    const date = this.value();
    if (!date) return "";
    return String(new Date(date).getMonth() + 1).padStart(2, "0");
  });

  yearValue = computed(() => {
    const date = this.value();
    if (!date) return "";
    return String(new Date(date).getFullYear());
  });

  // Valeurs des champs d'heure
  hourValue = computed(() => {
    const date = this.value();
    if (!date) return "";
    return String(new Date(date).getHours()).padStart(2, "0");
  });

  minuteValue = computed(() => {
    const date = this.value();
    if (!date) return "";
    return String(new Date(date).getMinutes()).padStart(2, "0");
  });

  onChangeFn = (v) => {};
  onTouchFn = (v) => {};

  /**
   * Parse date input as LOCAL midnight when it contains a YYYY-MM-DD pattern.
   * Avoids timezone shift: `new Date("2026-04-09")` creates UTC midnight,
   * but `.getDate()` uses local timezone → can shift the displayed day.
   */
  private toLocalDate(obj: any): Date {
    if (typeof obj === "string") {
      const m = obj.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) return new Date(+m[1], +m[2] - 1, +m[3], 0, 0, 0, 0);
    }
    return new Date(obj);
  }

  /** Format a local Date as "YYYY-MM-DD" string (no timezone shift). */
  private toIsoDateString(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByForm.set(isDisabled);
  }

  updateValue(value: Date) {
    this.value.set(value);
    if (this.mode() === "date" || this.dateOnly()) {
      this.displayValue.set(formatDate(value, "yyyy-MM-dd", "fr"));
    } else {
      this.displayValue.set(formatDate(value, "yyyy-MM-ddTHH:mm", "fr"));
    }
  }

  writeValue(obj: any): void {
    if (obj) {
      const date = this.toLocalDate(obj);
      this.buildedValue.set(date);
      this.updateValue(date);
    } else {
      this.value.set(null);
      this.displayValue.set("");
      this.buildedValue.set(new Date());
    }
  }

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
    effect(
      () => {
        if (this.type() === "chips") {
          this.selectedDate.set(null);
          this.hour.set(0);
          this.minute.set(0);
        }
      },
      { allowSignalWrites: true },
    );
    effect(
      () => {
        const displayValue = this.displayValue();
        if (displayValue) {
          const date = this.toLocalDate(displayValue);
          if (untracked(() => this.value()) !== date) {
            this.value.set(date);
            this.onChangeFn(this.dateOnly() ? this.toIsoDateString(date) : this.value());
          }
        }
      },
      { allowSignalWrites: true },
    );
    effect(
      () => {
        //changement de l'input value
        const date = this.value();
        if (date) {
          if (this.mode() === "date" || this.dateOnly()) {
            this.displayValue.set(formatDate(date, "yyyy-MM-dd", "fr"));
          } else {
            this.displayValue.set(formatDate(date, "yyyy-MM-ddTHH:mm", "fr"));
          }
          //selection menu
          this.selectedDate.set(new Date(date));
          this.hour.set(new Date(date).getHours());
          this.minute.set(new Date(date).getMinutes());
        }
      },
      { allowSignalWrites: true },
    );

    effect(() => {
      // mise à jour de buildedValue en fonction de la selection
      const date = new Date(this.selectedDate());
      date.setHours(this.hour(), this.minute(), 0, 0);
      if (untracked(() => this.buildedValue()) !== date) {
        asapScheduler.schedule(() => {
          this.buildedValue.set(date);
        });
      }
    });
  }

  onCalendarClick(event: MouseEvent) {
    event.stopPropagation();
    const target = event.target as HTMLElement;
    if (target.classList.contains("mat-calendar-body-selected")) {
      this.onDateSelected(this.selectedDate());
    }
  }

  hourUp() {
    if (this.hour() == 23) {
      this.hour.set(0);
      return;
    }
    this.hour.update((h) => h + 1);
  }

  hourDown() {
    if (this.hour() == 0) {
      this.hour.set(23);
      return;
    }
    this.hour.update((h) => h - 1);
  }

  minuteUp() {
    if (this.minute() == 59) {
      this.minute.set(0);
      return;
    }
    this.minute.update((h) => h + 1);
  }

  minuteDown() {
    if (this.minute() == 0) {
      this.minute.set(59);
      return;
    }
    this.minute.update((h) => h - 1);
  }

  onValid() {
    // On recompute directement depuis les signaux à jour (selectedDate / hour / minute)
    // car `buildedValue` est mis à jour de façon asynchrone via asapScheduler
    // → lire `buildedValue` ici peut renvoyer l'ancienne valeur si l'utilisateur
    // vient de modifier l'heure/minute juste avant de cliquer Valider.
    const base = this.selectedDate() ?? new Date();
    const val = new Date(base);
    val.setHours(this.hour() ?? 0, this.minute() ?? 0, 0, 0);
    this.buildedValue.set(val);
    this.updateValue(val);
    this.onChangeFn(this.dateOnly() ? this.toIsoDateString(val) : val);
    this.menuTrigger().closeMenu();
  }

  onClear() {
    this.buildedValue.set(null);
    this.value.set(null);
    this.menuTrigger().closeMenu();
  }

  onDateSelected(date: Date) {
    this.selectedDate.set(date);
    if (this.dateOnly()) {
      const newDate = new Date(date);
      newDate.setHours(0, 0, 0, 0);
      this.updateValue(newDate);
      this.onChangeFn(this.toIsoDateString(newDate));
      this.menuTrigger().closeMenu();
    }
  }

  onDayInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let digits = input.value.replace(/[^\d]/g, "");

    if (digits.length === 1) {
      const firstDigit = parseInt(digits, 10);
      // 4-9 ne peut être qu'un jour < 10, on auto-complète
      if (firstDigit >= 4 && firstDigit <= 9) {
        digits = "0" + digits;
        input.value = digits;
        this.monthInput()?.nativeElement.focus();
        this.tryBuildDate();
        return;
      }
    }

    if (digits.length >= 2) {
      const day = parseInt(digits.slice(0, 2), 10);
      // Valider le jour (1-31)
      if (day < 1 || day > 31) {
        digits = digits.slice(0, 1);
      } else {
        digits = digits.slice(0, 2);
        input.value = digits;
        this.monthInput()?.nativeElement.focus();
        this.tryBuildDate();
        return;
      }
    }

    input.value = digits;
    this.tryBuildDate();
  }

  onMonthInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let digits = input.value.replace(/[^\d]/g, "");

    if (digits.length === 1) {
      const firstDigit = parseInt(digits, 10);
      // 2-9 ne peut être qu'un mois < 10, on auto-complète
      if (firstDigit >= 2 && firstDigit <= 9) {
        digits = "0" + digits;
        input.value = digits;
        this.focusYearInput();
        this.tryBuildDate();
        return;
      }
    }

    if (digits.length >= 2) {
      const month = parseInt(digits.slice(0, 2), 10);
      // Valider le mois (1-12)
      if (month < 1 || month > 12) {
        digits = digits.slice(0, 1);
      } else {
        digits = digits.slice(0, 2);
        input.value = digits;
        this.focusYearInput();
        this.tryBuildDate();
        return;
      }
    }

    input.value = digits;
    this.tryBuildDate();
  }

  private focusYearInput() {
    const yearEl = this.yearInput()?.nativeElement;
    if (yearEl) {
      yearEl.focus();
    }
  }

  private focusHourInput() {
    const hourEl = this.hourInput()?.nativeElement;
    if (hourEl) {
      hourEl.focus();
    }
  }

  private focusMinuteInput() {
    const minuteEl = this.minuteInput()?.nativeElement;
    if (minuteEl) {
      minuteEl.focus();
    }
  }

  onHourInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let digits = input.value.replace(/[^\d]/g, "");

    if (digits.length === 1) {
      const firstDigit = parseInt(digits, 10);
      // 3-9 ne peut être qu'une heure < 10, on auto-complète
      if (firstDigit >= 3 && firstDigit <= 9) {
        digits = "0" + digits;
        input.value = digits;
        this.focusMinuteInput();
        this.tryBuildDate();
        return;
      }
    }

    if (digits.length >= 2) {
      const hour = parseInt(digits.slice(0, 2), 10);
      // Valider l'heure (0-23)
      if (hour > 23) {
        digits = digits.slice(0, 1);
      } else {
        digits = digits.slice(0, 2);
        input.value = digits;
        this.focusMinuteInput();
        this.tryBuildDate();
        return;
      }
    }

    input.value = digits;
    this.tryBuildDate();
  }

  onMinuteInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let digits = input.value.replace(/[^\d]/g, "");

    if (digits.length === 1) {
      const firstDigit = parseInt(digits, 10);
      // 6-9 ne peut être qu'une minute < 10, on auto-complète
      if (firstDigit >= 6 && firstDigit <= 9) {
        digits = "0" + digits;
        input.value = digits;
        this.tryBuildDate();
        return;
      }
    }

    if (digits.length >= 2) {
      const minute = parseInt(digits.slice(0, 2), 10);
      // Valider les minutes (0-59)
      if (minute > 59) {
        digits = digits.slice(0, 1);
      } else {
        digits = digits.slice(0, 2);
      }
    }

    input.value = digits;
    this.tryBuildDate();
  }

  onTimeKeydown(event: KeyboardEvent, field: "hour" | "minute") {
    const input = event.target as HTMLInputElement;

    if (
      event.key === "Backspace" &&
      input.selectionStart === 0 &&
      input.selectionEnd === 0
    ) {
      event.preventDefault();
      if (field === "minute") {
        this.hourInput()?.nativeElement.focus();
      } else if (field === "hour") {
        this.yearInput()?.nativeElement.focus();
      }
    }

    if (event.key === "ArrowLeft" && input.selectionStart === 0) {
      if (field === "minute") {
        this.hourInput()?.nativeElement.focus();
      } else if (field === "hour") {
        this.yearInput()?.nativeElement.focus();
      }
    }

    if (
      event.key === "ArrowRight" &&
      input.selectionStart === input.value.length
    ) {
      if (field === "hour") {
        this.focusMinuteInput();
      }
    }
  }

  onYearInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/[^\d]/g, "").slice(0, 4);
    input.value = digits;

    // Si 4 chiffres et pas en dateOnly, passer à l'heure
    if (digits.length === 4 && !this.dateOnly()) {
      this.focusHourInput();
    }

    this.tryBuildDate();
  }

  onFieldFocus(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    if (input.value) {
      setTimeout(() => input.select());
    }
  }

  onYearFocus(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    if (!input.value) {
      input.value = "20";
      // Placer le curseur après "20"
      setTimeout(() => input.setSelectionRange(2, 2));
    } else if (input.value.length === 4) {
      // En édition : sélectionner les 2 derniers chiffres
      setTimeout(() => input.setSelectionRange(2, 4));
    }
  }

  onYearClick(event: MouseEvent) {
    const input = event.target as HTMLInputElement;
    if (input.value) {
      setTimeout(() => input.select());
    }
  }

  onDateKeydown(event: KeyboardEvent, field: "day" | "month" | "year") {
    const input = event.target as HTMLInputElement;

    if (
      event.key === "Backspace" &&
      input.selectionStart === 0 &&
      input.selectionEnd === 0
    ) {
      event.preventDefault();
      if (field === "month") {
        this.dayInput()?.nativeElement.focus();
      } else if (field === "year") {
        this.monthInput()?.nativeElement.focus();
      }
    }

    if (event.key === "ArrowLeft" && input.selectionStart === 0) {
      if (field === "month") {
        this.dayInput()?.nativeElement.focus();
      } else if (field === "year") {
        this.monthInput()?.nativeElement.focus();
      }
    }

    if (
      event.key === "ArrowRight" &&
      input.selectionStart === input.value.length
    ) {
      if (field === "day") {
        this.monthInput()?.nativeElement.focus();
      } else if (field === "month") {
        const yearEl = this.yearInput()?.nativeElement;
        if (yearEl) {
          yearEl.focus();
          if (!yearEl.value) {
            yearEl.value = "20";
            setTimeout(() => yearEl.setSelectionRange(2, 2));
          }
        }
      } else if (field === "year" && !this.dateOnly()) {
        this.focusHourInput();
      }
    }
  }

  private tryBuildDate(): boolean {
    const dayEl = this.dayInput()?.nativeElement;
    const monthEl = this.monthInput()?.nativeElement;
    const yearEl = this.yearInput()?.nativeElement;

    if (!dayEl || !monthEl || !yearEl) return false;

    const day = parseInt(dayEl.value, 10);
    const month = parseInt(monthEl.value, 10);
    const year = parseInt(yearEl.value, 10);

    const isDateComplete =
      dayEl.value.length === 2 &&
      monthEl.value.length === 2 &&
      yearEl.value.length === 4 &&
      day >= 1 &&
      day <= 31 &&
      month >= 1 &&
      month <= 12 &&
      year >= 1900;

    if (!isDateComplete) return false;

    // Si dateOnly, on n'a pas besoin de l'heure
    if (this.dateOnly()) {
      const date = new Date(year, month - 1, day, 0, 0, 0, 0);
      if (!isNaN(date.getTime())) {
        this.displayValue.set(formatDate(date, "yyyy-MM-dd", "fr"));
        this.value.set(date);
        this.onChangeFn(this.toIsoDateString(date));
        return true;
      }
      return false;
    }

    // Mode datetime : vérifier aussi l'heure et les minutes
    const hourEl = this.hourInput()?.nativeElement;
    const minuteEl = this.minuteInput()?.nativeElement;

    if (!hourEl || !minuteEl) return false;

    const hour = parseInt(hourEl.value, 10);
    const minute = parseInt(minuteEl.value, 10);

    const isTimeComplete =
      hourEl.value.length === 2 &&
      minuteEl.value.length === 2 &&
      hour >= 0 &&
      hour <= 23 &&
      minute >= 0 &&
      minute <= 59;

    if (isTimeComplete) {
      const date = new Date(year, month - 1, day, hour, minute, 0, 0);
      if (!isNaN(date.getTime())) {
        this.displayValue.set(formatDate(date, "yyyy-MM-ddTHH:mm", "fr"));
        this.value.set(date);
        this.onChangeFn(date);
        return true;
      }
    }

    return false;
  }

  onDateFieldBlur(event: FocusEvent) {
    const container = event.currentTarget as HTMLElement;
    const relatedTarget = event.relatedTarget as HTMLElement;

    // Si le focus reste dans le conteneur, ne pas valider
    if (relatedTarget && container.contains(relatedTarget)) {
      return;
    }

    // Auto-pad les saisies à 1 chiffre (ex: heure "2" → "02") avant de valider —
    // sans ça, tryBuildDate refuse car il exige strictement length === 2.
    this.padInputsToTwoDigits();

    // On quitte le composant : vérifier si la date est complète
    const isValid = this.tryBuildDate();

    if (!isValid && this.value() !== null) {
      // Date incomplète -> null
      this.value.set(null);
      this.displayValue.set("");
      this.onChangeFn(null);
    }

    this.onTouchFn(true);
  }

  /** Pad à 2 chiffres les inputs jj/mm/hh/mm pour accepter une saisie à 1 chiffre. */
  private padInputsToTwoDigits() {
    const pad = (el: HTMLInputElement | undefined) => {
      if (el && el.value.length === 1 && /^\d$/.test(el.value)) {
        el.value = "0" + el.value;
      }
    };
    pad(this.dayInput()?.nativeElement);
    pad(this.monthInput()?.nativeElement);
    pad(this.hourInput()?.nativeElement);
    pad(this.minuteInput()?.nativeElement);
  }
}
